import { useEffect, useRef, useState } from "react";
import { useContent } from "../content/ContentContext";
import API, { apiErrorMessage } from "../lib/api";
import { euro } from "../lib/format";

const inputCls =
  "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink transition-colors focus:border-accent";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted">{hint}</span>}
    </label>
  );
}

function LineArea({ value, onChange, rows = 4, hint }) {
  return (
    <Field hint={hint}>
      <textarea
        className={inputCls}
        rows={rows}
        value={Array.isArray(value) ? value.join("\n") : value || ""}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.replace(/\r/, "")))}
      />
    </Field>
  );
}

function SaveBar({ onSave, saving, onReset, dirty }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="sticky bottom-0 -mx-2 flex flex-wrap items-center gap-3 border-t border-line bg-cream/95 px-2 py-3 backdrop-blur">
      <button
        data-testid="editor-save-button"
        onClick={async () => { await onSave(); setSaved(true); setTimeout(() => setSaved(false), 2500); }}
        disabled={saving}
        className="rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save changes"}
      </button>
      {onReset && (
        <button
          data-testid="editor-reset-button"
          onClick={onReset}
          className="rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-plum hover:text-plum"
        >
          Reset section to original
        </button>
      )}
      {saved && <span data-testid="editor-saved-indicator" className="text-sm font-semibold text-accent">Saved — the live site now shows your changes.</span>}
      {!saved && dirty && <span className="text-sm text-muted">Unsaved changes</span>}
    </div>
  );
}

function Collapsible({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-line bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span>
          <span className="block font-serif text-base font-medium text-ink">{title}</span>
          {subtitle && <span className="block text-xs text-muted">{subtitle}</span>}
        </span>
        <span aria-hidden="true" className={`text-xl text-accent transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <div className="space-y-4 border-t border-line px-5 py-5">{children}</div>}
    </div>
  );
}

function useSection(key, localValue) {
  const { refresh } = useContent();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async (value) => {
    try {
      setSaving(true);
      await API.put(`/owner/content/${key}`, { value });
      await refresh();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "Saving failed."));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    try {
      setSaving(true);
      await API.post(`/owner/content/${key}/reset`);
      await refresh();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "Reset failed."));
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { save, reset, saving, error };
}

function SiteEditor({ site }) {
  const [v, setV] = useState(site);
  const { save, reset, saving, error } = useSection("site");
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const setPortrait = (k) => (e) => setV({ ...v, aboutPortrait: { ...v.aboutPortrait, [k]: e.target.value } });
  const share = v.share || { title: "", description: "", image: "/images/og-image.png" };
  const setShare = (k) => (e) => setV({ ...v, share: { ...share, [k]: e.target.value } });
  return (
    <div className="space-y-5">
      <Field label="Headline (one line per row)">
        <textarea className={inputCls} rows={3} value={v.headline.join("\n")}
          onChange={(e) => setV({ ...v, headline: e.target.value.split("\n") })} />
      </Field>
      <Field label="Supporting copy (hero)">
        <textarea className={inputCls} rows={2} value={v.sub} onChange={set("sub")} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Role line"><input className={inputCls} value={v.role} onChange={set("role")} /></Field>
        <Field label="Location line"><input className={inputCls} value={v.location} onChange={set("location")} /></Field>
      </div>
      <Field label="Contact email"><input className={inputCls} value={v.email} onChange={set("email")} /></Field>
      <Field label="LinkedIn profile URL"><input data-testid="editor-linkedin-input" className={inputCls} value={v.linkedin || ""} onChange={set("linkedin")} /></Field>
      <div className="rounded-2xl border border-line p-4" data-testid="editor-share">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Social sharing preview</p>
        <p className="mb-3 text-[11px] leading-relaxed text-muted">
          How the website appears when its link is shared on LinkedIn, WhatsApp or Facebook. Not shown on the site itself.
          The image must be 1200 × 630 pixels; upload one on the Images tab and press “Set as share image”, or keep the default branded graphic.
        </p>
        <div className="space-y-3">
          <Field label="Preview title"><input data-testid="editor-share-title" className={inputCls} value={share.title} onChange={setShare("title")} /></Field>
          <Field label="Preview description"><textarea data-testid="editor-share-description" className={inputCls} rows={2} value={share.description} onChange={setShare("description")} /></Field>
          <Field label="Preview image URL"><input data-testid="editor-share-image" className={inputCls} value={share.image} onChange={setShare("image")} /></Field>
          <img src={share.image} alt="Current social sharing preview" data-testid="editor-share-image-preview" className="w-full max-w-sm rounded-xl border border-line" />
        </div>
      </div>
      <LineArea label={undefined} value={v.aboutIntro} onChange={(x) => setV({ ...v, aboutIntro: x })} rows={5}
        hint="About paragraphs — one paragraph per row (blank line between paragraphs)." />
      <div className="rounded-2xl border border-line p-4">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Section headings &amp; introductions</p>
        <div className="space-y-3">
          <Field label="Homepage services heading"><input data-testid="editor-services-heading" className={inputCls} value={v.servicesHeading} onChange={set("servicesHeading")} /></Field>
          <Field label="Homepage services introduction"><textarea className={inputCls} rows={2} value={v.servicesIntro} onChange={set("servicesIntro")} /></Field>
          <Field label="Services small print (under the introduction)"><textarea className={inputCls} rows={2} value={v.servicesPageIntro} onChange={set("servicesPageIntro")} /></Field>
          <Field label="Case Studies page heading"><input data-testid="editor-cases-heading" className={inputCls} value={v.caseStudiesHeading} onChange={set("caseStudiesHeading")} /></Field>
          <Field label="Case Studies introduction"><textarea className={inputCls} rows={2} value={v.caseStudiesIntro} onChange={set("caseStudiesIntro")} /></Field>
          <Field label="Case Studies sample-data note"><input className={inputCls} value={v.caseStudiesNote} onChange={set("caseStudiesNote")} /></Field>
          <Field label="Pricing heading"><input className={inputCls} value={v.pricingHeading} onChange={set("pricingHeading")} /></Field>
          <Field label="Pricing introduction"><textarea className={inputCls} rows={2} value={v.pricingIntro} onChange={set("pricingIntro")} /></Field>
          <Field label="Pricing small print"><textarea className={inputCls} rows={2} value={v.pricingNote} onChange={set("pricingNote")} /></Field>
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4" data-testid="editor-approach">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">My approach section</p>
        <div className="space-y-3">
          <Field label="Small label"><input className={inputCls} value={v.approach.label} onChange={(e) => setV({ ...v, approach: { ...v.approach, label: e.target.value } })} /></Field>
          <Field label="Heading"><input data-testid="editor-approach-heading" className={inputCls} value={v.approach.heading} onChange={(e) => setV({ ...v, approach: { ...v.approach, heading: e.target.value } })} /></Field>
          <Field label="Introduction"><textarea className={inputCls} rows={3} value={v.approach.intro} onChange={(e) => setV({ ...v, approach: { ...v.approach, intro: e.target.value } })} /></Field>
          {v.approach.stages.map((st, i) => (
            <div key={i} className="grid grid-cols-1 gap-3 rounded-xl bg-cream/60 p-3 sm:grid-cols-[1fr_2fr]">
              <Field label={`Stage 0${i + 1} title`}><input className={inputCls} value={st.title} onChange={(e) => setV({ ...v, approach: { ...v.approach, stages: v.approach.stages.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) } })} /></Field>
              <Field label="Text"><textarea className={inputCls} rows={2} value={st.body} onChange={(e) => setV({ ...v, approach: { ...v.approach, stages: v.approach.stages.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) } })} /></Field>
            </div>
          ))}
          <Field label="Tools & methods label"><input className={inputCls} value={v.approach.toolsLabel} onChange={(e) => setV({ ...v, approach: { ...v.approach, toolsLabel: e.target.value } })} /></Field>
          <LineArea value={v.approach.tools} onChange={(x) => setV({ ...v, approach: { ...v.approach, tools: x } })} rows={6} hint="Tools & methods tags — one per row, shown in this order." />
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4" data-testid="editor-card-colours">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Service &amp; package card colours</p>
        <p className="mb-3 text-[11px] leading-relaxed text-muted">Default, hover and selected backgrounds; purple border; recommended-package background; heading, body text and tick colours.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["default", "Default background"], ["hover", "Hover background"], ["selected", "Selected background"], ["border", "Hover / selected border"],
            ["recommendedBg", "Recommended background"], ["heading", "Headings & price"], ["body", "Body text"], ["check", "Checkmarks"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <div className="flex items-center gap-2">
                <input type="color" data-testid={`editor-card-colour-${k}`} className="h-9 w-10 cursor-pointer rounded-lg border border-line bg-white p-0.5"
                  value={v.cardColors?.[k] || "#000000"} onChange={(e) => setV({ ...v, cardColors: { ...v.cardColors, [k]: e.target.value } })} />
                <input className={inputCls} value={v.cardColors?.[k] || ""} onChange={(e) => setV({ ...v, cardColors: { ...v.cardColors, [k]: e.target.value } })} />
              </div>
            </Field>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4" data-testid="editor-chart-palette">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Chart palette (case-study visuals)</p>
        <p className="mb-3 text-[11px] leading-relaxed text-muted">
          Navy = primary metrics · Blue = secondary series · Burnt orange = highlights &amp; bottlenecks ·
          Teal = positive outcomes · Dark red = exceptions (sparingly) · Slate = baselines / previous process · Lavender = supporting accent.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["navy", "Navy"], ["blue", "Blue"], ["orange", "Burnt orange"], ["teal", "Teal"], ["red", "Dark red"], ["slate", "Slate grey"], ["lavender", "Lavender"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <div className="flex items-center gap-2">
                <input type="color" data-testid={`editor-palette-${k}`} className="h-9 w-10 cursor-pointer rounded-lg border border-line bg-white p-0.5"
                  value={v.chartPalette?.[k] || "#000000"} onChange={(e) => setV({ ...v, chartPalette: { ...v.chartPalette, [k]: e.target.value } })} />
                <input className={inputCls} value={v.chartPalette?.[k] || ""} onChange={(e) => setV({ ...v, chartPalette: { ...v.chartPalette, [k]: e.target.value } })} />
              </div>
            </Field>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Service categories (Services page)</p>
        <div className="space-y-3">
          {v.serviceGroups.map((g, i) => (
            <Field key={g.id} label={`Category ${i + 1} heading`}>
              <input className={inputCls} value={g.title} onChange={(e) => setV({ ...v, serviceGroups: v.serviceGroups.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />
            </Field>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Homepage hourly &amp; automation box</p>
        <div className="space-y-3">
          <Field label="Heading"><input className={inputCls} value={v.hourlyBand.heading} onChange={(e) => setV({ ...v, hourlyBand: { ...v.hourlyBand, heading: e.target.value } })} /></Field>
          <Field label="Text"><textarea className={inputCls} rows={2} value={v.hourlyBand.body} onChange={(e) => setV({ ...v, hourlyBand: { ...v.hourlyBand, body: e.target.value } })} /></Field>
        </div>
      </div>
      <div className="rounded-2xl border border-line p-4">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">“Need several services together?” box (Services page)</p>
        <div className="space-y-3">
          <Field label="Heading"><input className={inputCls} value={v.packagesLinks.heading} onChange={(e) => setV({ ...v, packagesLinks: { ...v.packagesLinks, heading: e.target.value } })} /></Field>
          <Field label="Text"><textarea className={inputCls} rows={2} value={v.packagesLinks.body} onChange={(e) => setV({ ...v, packagesLinks: { ...v.packagesLinks, body: e.target.value } })} /></Field>
          <LineArea value={v.packagesLinks.items} onChange={(x) => setV({ ...v, packagesLinks: { ...v.packagesLinks, items: x } })} rows={5} hint="Package summary lines — one per row." />
        </div>
      </div>
      <Field label="Tools & methods (comma separated)"><input className={inputCls} value={v.tools.join(", ")} onChange={(e) => setV({ ...v, tools: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
      <div className="rounded-2xl border border-line p-4">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">About portrait</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Image URL (use the Images tab to upload & crop, then paste its URL here)"><input className={inputCls} value={v.aboutPortrait.src} onChange={setPortrait("src")} /></Field>
          <Field label="Alternative text"><input className={inputCls} value={v.aboutPortrait.alt} onChange={setPortrait("alt")} /></Field>
          <Field label="Caption"><input className={inputCls} value={v.aboutPortrait.caption} onChange={setPortrait("caption")} /></Field>
          <Field label="Caption sub-line"><input className={inputCls} value={v.aboutPortrait.captionSub} onChange={setPortrait("captionSub")} /></Field>
        </div>
      </div>
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Experience highlights</p>
        <LineArea value={v.experienceHighlights} onChange={(x) => setV({ ...v, experienceHighlights: x })} rows={5} hint="One highlight per row." />
      </div>
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Engagement process (4 steps)</p>
        {v.process.map((p, i) => (
          <div key={i} className="grid grid-cols-1 gap-3 rounded-xl border border-line p-3 sm:grid-cols-2">
            <Field label={`Step ${i + 1} title`}><input className={inputCls} value={p.title} onChange={(e) => setV({ ...v, process: v.process.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} /></Field>
            <Field label="Description"><input className={inputCls} value={p.body} onChange={(e) => setV({ ...v, process: v.process.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) })} /></Field>
          </div>
        ))}
      </div>
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar onSave={() => save(v)} saving={saving} onReset={reset} />
    </div>
  );
}

function OrderControls({ index, total, onMove, onRemove, name }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      <button type="button" disabled={index === 0} onClick={() => onMove(index, index - 1)} data-testid={`editor-move-up-${index}`}
        className="rounded-full border border-line px-3 py-1 font-semibold text-ink hover:border-plum hover:text-plum disabled:opacity-40">↑ Move up</button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(index, index + 1)} data-testid={`editor-move-down-${index}`}
        className="rounded-full border border-line px-3 py-1 font-semibold text-ink hover:border-plum hover:text-plum disabled:opacity-40">↓ Move down</button>
      <button type="button" onClick={() => { if (window.confirm(`Remove “${name}”?`)) onRemove(index); }}
        className="font-semibold text-plum underline underline-offset-4">Remove this item</button>
    </div>
  );
}

function useListEditor(initial, sectionKey) {
  const [list, setList] = useState(initial.map((x) => ({ ...x })));
  const section = useSection(sectionKey);
  const upd = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (from, to) => setList((l) => { const c = [...l]; const [it] = c.splice(from, 1); c.splice(to, 0, it); return c; });
  const remove = (i) => setList((l) => l.filter((_, j) => j !== i));
  const add = (item) => setList((l) => [...l, item]);
  return { list, setList, upd, move, remove, add, ...section };
}

const HiddenToggle = ({ value, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-ink">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    Hide from the public website
  </label>
);

function ServicesEditor({ services, groups }) {
  const { list, upd, move, remove, add, save, reset, saving, error } = useListEditor(services, "services");
  return (
    <div className="space-y-4">
      <p className="rounded-xl bg-lavender/70 p-4 text-xs leading-relaxed text-plum">
        These cards appear on the Services page, grouped by category, and their names fill the enquiry
        form dropdown. Prices are fixed euro prices — type them exactly as they should read (e.g. “From €400”).
      </p>
      {list.map((s, i) => (
        <Collapsible key={s.id || i} title={s.name || "(untitled)"} subtitle={s.priceText}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name"><input className={inputCls} value={s.name} onChange={(e) => upd(i, { name: e.target.value })} /></Field>
            <Field label="Price (as displayed)"><input data-testid={`editor-service-price-${s.id}`} className={inputCls} value={s.priceText || ""} onChange={(e) => upd(i, { priceText: e.target.value })} /></Field>
          </div>
          <Field label="Category">
            <select className={inputCls} value={s.group} onChange={(e) => upd(i, { group: e.target.value })}>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
          </Field>
          <Field label="Short description"><textarea className={inputCls} rows={2} value={s.summary} onChange={(e) => upd(i, { summary: e.target.value })} /></Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Deliverables heading"><input className={inputCls} value={s.includesLabel || "Includes"} onChange={(e) => upd(i, { includesLabel: e.target.value })} /></Field>
            <Field label="Minimum (optional)"><input className={inputCls} value={s.minimumNote || ""} onChange={(e) => upd(i, { minimumNote: e.target.value })} /></Field>
          </div>
          <LineArea value={s.includes || []} onChange={(x) => upd(i, { includes: x })} hint="Deliverables — one per row." />
          <Field label="Scope note"><textarea className={inputCls} rows={2} value={s.scopeNote || ""} onChange={(e) => upd(i, { scopeNote: e.target.value })} /></Field>
          <Field label="Button label"><input className={inputCls} value={s.cta} onChange={(e) => upd(i, { cta: e.target.value })} /></Field>
          <HiddenToggle value={s.hidden} onChange={(v) => upd(i, { hidden: v })} />
          <OrderControls index={i} total={list.length} onMove={move} onRemove={remove} name={s.name} />
        </Collapsible>
      ))}
      <button data-testid="editor-add-item"
        onClick={() => add({ id: `service-${Date.now()}`, hidden: true, group: groups[0]?.id, name: "New service", priceText: "From €0",
          summary: "", includesLabel: "Includes", includes: [], minimumNote: "", scopeNote: "", cta: "Discuss this service" })}
        className="rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-plum hover:text-plum">
        + Add service
      </button>
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar onSave={() => save(list)} saving={saving} onReset={reset} />
    </div>
  );
}

function PackagesEditor({ packages }) {
  const { list, setList, upd, move, remove, add, save, reset, saving, error } = useListEditor(packages, "packages");
  return (
    <div className="space-y-4">
      {list.map((s, i) => (
        <Collapsible key={s.id || i} title={s.name || "(untitled)"} subtitle={`${euro(s.priceFrom)} ${s.priceLabel || ""}`}>
          <Field label="Name"><input className={inputCls} value={s.name} onChange={(e) => upd(i, { name: e.target.value })} /></Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Price (€)"><input type="number" className={inputCls} value={s.priceFrom ?? ""} onChange={(e) => upd(i, { priceFrom: Number(e.target.value) })} /></Field>
            <Field label="Price label (e.g. from, per month, per hour)"><input data-testid={`editor-price-label-${s.id}`} className={inputCls} value={s.priceLabel || ""} onChange={(e) => upd(i, { priceLabel: e.target.value })} /></Field>
          </div>
          <Field label="Short description"><textarea className={inputCls} rows={2} value={s.summary} onChange={(e) => upd(i, { summary: e.target.value })} /></Field>
          <LineArea value={s.includes || []} onChange={(x) => upd(i, { includes: x })} hint="What's included — one per row (shown with a tick)." />
          <Field label="Scope note (shown at the bottom of the card)"><textarea className={inputCls} rows={2} value={s.footnote || ""} onChange={(e) => upd(i, { footnote: e.target.value })} /></Field>
          <Field label="Button label"><input className={inputCls} value={s.cta} onChange={(e) => upd(i, { cta: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" data-testid={`editor-package-recommended-${s.id}`} checked={!!s.recommended}
              onChange={(e) => setList((l) => l.map((x, j) => ({ ...x, recommended: j === i ? e.target.checked : false })))} />
            Recommended package (rich lavender card with a “Recommended” badge — only one at a time)
          </label>
          <HiddenToggle value={s.hidden} onChange={(v) => upd(i, { hidden: v })} />
          <OrderControls index={i} total={list.length} onMove={move} onRemove={remove} name={s.name} />
        </Collapsible>
      ))}
      <button data-testid="editor-add-item"
        onClick={() => add({ id: `package-${Date.now()}`, name: "New package", priceFrom: 500, priceLabel: "from", hidden: true, summary: "", includes: [], footnote: "", cta: "Discuss this package" })}
        className="rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-plum hover:text-plum">
        + Add package
      </button>
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar onSave={() => save(list)} saving={saving} onReset={reset} />
    </div>
  );
}

function CaseStudiesEditor({ caseStudies, services, packages }) {
  const [list, setList] = useState(caseStudies.map((c) => ({ ...c, resultsText: c.results.map((r) => r.note ? `${r.text} | ${r.note}` : r.text) })));
  const { save, reset, saving, error } = useSection("caseStudies");
  const upd = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const visuals = ["invoice-recon", "returns", "supply-chain", "automation", "process"];
  return (
    <div className="space-y-4">
      <p className="rounded-xl bg-lavender/70 p-4 text-xs leading-relaxed text-plum">
        Results: one per row. Keep results in their original currency and only where you have
        evidence. Add “ | your note” after a result to show a small note under it.
      </p>
      {list.map((c, i) => (
        <Collapsible key={c.id || i} title={c.title || "(untitled)"} subtitle={c.area}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title"><input data-testid={`editor-cs-title-${c.id}`} className={inputCls} value={c.title} onChange={(e) => upd(i, { title: e.target.value })} /></Field>
            <Field label="Topic label (shown next to the case number)"><input className={inputCls} value={c.topic || c.area} onChange={(e) => upd(i, { topic: e.target.value, area: e.target.value })} /></Field>
          </div>
          <div className="rounded-xl border border-line p-3">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Case Studies page — two-column intro</p>
            <Field label="Business problem and why it mattered"><textarea className={inputCls} rows={3} value={c.problem} onChange={(e) => upd(i, { problem: e.target.value })} /></Field>
            <LineArea value={c.whatIDid || []} onChange={(x) => upd(i, { whatIDid: x })} rows={5} hint="What I did — three to five bullets, one per row." />
            <Field label="Result statement"><textarea data-testid={`editor-cs-result-${c.id}`} className={inputCls} rows={2} value={c.result || ""} onChange={(e) => upd(i, { result: e.target.value })} /></Field>
          </div>
          <Field label="Card summary (problem and outcome)"><textarea className={inputCls} rows={2} value={c.summary} onChange={(e) => upd(i, { summary: e.target.value })} /></Field>
          <div className="rounded-xl border border-line p-3" data-testid={`editor-cs-share-${c.id}`}>
            <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Social sharing (when this case study's own link is shared)</p>
            <p className="mb-2 text-[11px] leading-relaxed text-muted">Optional. Leave blank to use “{c.title} — Case Study” and the card summary above, with the site's default share image.</p>
            <div className="space-y-3">
              <Field label="Share title"><input data-testid={`editor-cs-share-title-${c.id}`} className={inputCls} value={c.shareTitle || ""} placeholder={`${c.title} — Case Study`} onChange={(e) => upd(i, { shareTitle: e.target.value })} /></Field>
              <Field label="Share description"><textarea data-testid={`editor-cs-share-description-${c.id}`} className={inputCls} rows={2} value={c.shareDescription || ""} placeholder={c.summary} onChange={(e) => upd(i, { shareDescription: e.target.value })} /></Field>
              <Field label="Share image URL (1200 × 630 — upload on the Images tab and paste its /api/images/… URL)"><input data-testid={`editor-cs-share-image-${c.id}`} className={inputCls} value={c.shareImage || ""} placeholder="/images/og-image.png (default)" onChange={(e) => upd(i, { shareImage: e.target.value })} /></Field>
            </div>
          </div>
          <Field label="Intro"><textarea className={inputCls} rows={2} value={c.intro} onChange={(e) => upd(i, { intro: e.target.value })} /></Field>
          <Field label="Business context"><textarea className={inputCls} rows={3} value={c.context} onChange={(e) => upd(i, { context: e.target.value })} /></Field>
          <Field label="My role"><textarea className={inputCls} rows={3} value={c.role} onChange={(e) => upd(i, { role: e.target.value })} /></Field>
          <LineArea label={undefined} value={c.approach} onChange={(x) => upd(i, { approach: x })} hint="Approach steps — one per row." />
          <LineArea label={undefined} value={c.deliverables} onChange={(x) => upd(i, { deliverables: x })} hint="Deliverables — one per row." />
          <LineArea label={undefined} value={c.resultsText} onChange={(x) => upd(i, { resultsText: x })} rows={3} />
          <LineArea label={undefined} value={c.findings} onChange={(x) => upd(i, { findings: x })} hint="Findings — one per row." />
          <Field label="Tools (comma separated)"><input className={inputCls} value={c.tools.join(", ")} onChange={(e) => upd(i, { tools: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Interactive visual">
              <select className={inputCls} value={c.visual} onChange={(e) => upd(i, { visual: e.target.value })}>
                {visuals.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Related service">
              <select className={inputCls} value={c.relatedService} onChange={(e) => upd(i, { relatedService: e.target.value })}>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Related package">
              <select className={inputCls} value={c.relatedPackage} onChange={(e) => upd(i, { relatedPackage: e.target.value })}>
                {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={!!c.hidden} onChange={(e) => upd(i, { hidden: e.target.checked })} />
            Hide from the public website
          </label>
          <button
            data-testid={`cs-editor-add`}
            onClick={() => setList((l) => [...l, {
              id: `case-${Date.now()}`, kind: "experience", hidden: true, title: "New case study",
              area: "Area", summary: "", intro: "", context: "", problem: "", role: "",
              approach: [], deliverables: [], results: [], resultsText: [], findings: [],
              tools: [], visual: "invoice-recon", relatedService: services[0]?.id, relatedPackage: packages[0]?.id,
            }])}
            className="text-xs font-semibold text-plum underline underline-offset-4"
          >
            + Add case study
          </button>
        </Collapsible>
      ))}
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar
        onSave={() => save(list.map(({ resultsText, ...c }) => ({
          ...c,
          results: (resultsText || []).filter(Boolean).map((line) => {
            const [text, note] = line.split("|");
            return { text: text.trim(), note: (note || "").trim() };
          }),
        })))}
        saving={saving}
        onReset={reset}
      />
    </div>
  );
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((s) => s.trim());
  return lines.slice(1).filter(Boolean).map((l) => {
    const cells = l.split(",");
    const o = {};
    header.forEach((h, i) => {
      const v = (cells[i] ?? "").trim();
      o[h] = v !== "" && !isNaN(Number(v)) ? Number(v) : v;
    });
    return o;
  });
}

function DataEditor({ dashboards }) {
  const keys = Object.keys(dashboards);
  const [dataset, setDataset] = useState(keys[0]);
  const [rows, setRows] = useState(dashboards[keys[0]] || []);
  const [csv, setCsv] = useState("");
  const { save, saving, error } = useSection("dashboards");
  const columns = rows.length ? Object.keys(rows[0]) : [];
  const switchTo = (k) => { setDataset(k); setRows(dashboards[k] || []); setCsv(""); };
  const upd = (ri, col, val) =>
    setRows((rs) => rs.map((r, i) => (i === ri ? { ...r, [col]: isNaN(Number(val)) || val === "" ? val : Number(val) } : r)));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Dataset">
          <select className={inputCls} value={dataset} onChange={(e) => switchTo(e.target.value)} data-testid="data-dataset-select">
            {keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <p className="text-xs text-muted">{rows.length} rows · the dashboards recalculate from this data.</p>
      </div>
      <div className="max-h-96 overflow-auto rounded-2xl border border-line" data-testid="data-table">
        <table className="w-full min-w-[720px] border-collapse text-left text-xs">
          <thead className="sticky top-0 bg-lavender/80">
            <tr className="font-mono text-[10px] uppercase tracking-[0.12em] text-plum">
              {columns.map((c) => <th key={c} className="px-2 py-2 font-medium">{c}</th>)}
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-line/70">
                {columns.map((c) => (
                  <td key={c} className="px-1 py-1">
                    <input className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-xs text-ink hover:border-line focus:border-accent focus:bg-white"
                      value={r[c]} onChange={(e) => upd(ri, c, e.target.value)} aria-label={`Row ${ri + 1} ${c}`} />
                  </td>
                ))}
                <td className="px-1 py-1 text-right">
                  <button onClick={() => setRows((rs) => rs.filter((_, i) => i !== ri))} aria-label={`Delete row ${ri + 1}`} className="px-1 text-muted hover:text-plum">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => setRows((rs) => [...rs, Object.fromEntries(columns.map((c) => [c, ""]))])}
          className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum">
          + Add row
        </button>
      </div>
      <div className="rounded-2xl border border-line bg-white p-4">
        <Field label="Import CSV (first row = column names, replaces the table above)" hint="You can also use a .csv file — its text is loaded here first so you can check it before importing.">
          <textarea className={inputCls} rows={3} value={csv} onChange={(e) => setCsv(e.target.value)} />
        </Field>
        <div className="mt-3 flex flex-wrap gap-3">
          <label className="cursor-pointer rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum">
            Choose .csv file
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => setCsv(String(reader.result));
              reader.readAsText(file);
            }} />
          </label>
          <button data-testid="data-import-csv" onClick={() => {
            const parsed = parseCsv(csv);
            if (parsed.length) setRows(parsed);
          }} className="rounded-full bg-plum px-4 py-2 text-xs font-semibold text-white hover:bg-ink">
            Import from text
          </button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar onSave={() => save({ ...dashboards, [dataset]: rows })} saving={saving} />
    </div>
  );
}

function ProcessEditor({ process }) {
  const [v, setV] = useState(JSON.parse(JSON.stringify(process)));
  const { save, reset, saving, error } = useSection("process");
  const updStep = (phase, li, si, patch) =>
    setV({ ...v, [phase]: { ...v[phase], lanes: v[phase].lanes.map((lane, li2) => li2 !== li ? lane : { ...lane, steps: lane.steps.map((s, si2) => (si2 !== si ? s : { ...s, ...patch })) }) } });
  const StepRows = ({ phase, li, steps }) => (
    <div className="space-y-3">
      {steps.map((s, si) => (
        <div key={s.id} className="space-y-3 rounded-xl border border-line p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Label"><input className={inputCls} value={s.label} onChange={(e) => updStep(phase, li, si, { label: e.target.value })} /></Field>
            <Field label="Responsible"><input className={inputCls} value={s.responsible} onChange={(e) => updStep(phase, li, si, { responsible: e.target.value })} /></Field>
          </div>
          <Field label="Description (shown when a visitor clicks the step)"><textarea className={inputCls} rows={2} value={s.desc} onChange={(e) => updStep(phase, li, si, { desc: e.target.value })} /></Field>
          <div className="flex flex-wrap gap-4 text-xs text-ink">
            {[["bottleneck", "Bottleneck"], ["exception", "Exception path"], ["automated", "Automated"], ["control", "Control retained"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={!!s[k]} onChange={(e) => updStep(phase, li, si, { [k]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
  const linesToStories = (arr) =>
    arr.map((us) => ({ ...us, ac: us.ac }));
  return (
    <div className="space-y-6">
      <Field label="Map title"><input className={inputCls} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} /></Field>
      {["asIs", "toBe"].map((phase) => (
        <div key={phase} className="rounded-2xl border border-line bg-white p-4">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">{phase === "asIs" ? "AS-IS map" : "TO-BE map"}</p>
          {v[phase].lanes.map((lane, li) => (
            <div key={lane.name} className="mb-4">
              <Field label="Lane name">
                <input className={inputCls} value={lane.name} onChange={(e) => setV({ ...v, [phase]: { ...v[phase], lanes: v[phase].lanes.map((l, j) => (j === li ? { ...l, name: e.target.value } : l)) } })} />
              </Field>
              <StepRows phase={phase} li={li} steps={lane.steps} />
            </div>
          ))}
        </div>
      ))}
      <LineArea label={undefined} value={v.requirements.map(([ref, text]) => `${ref} | ${text}`)}
        onChange={(x) => setV({ ...v, requirements: x.filter(Boolean).map((l) => { const [ref, ...rest] = l.split("|"); return [ref.trim(), rest.join("|").trim()]; }) })}
        rows={6} hint="Requirements — one per row: REF | requirement text" />
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">User stories</p>
        {linesToStories(v.userStories).map((us, i) => (
          <div key={us.id} className="space-y-3 rounded-xl border border-line p-3">
            <Field label="Story">
              <textarea className={inputCls} rows={2} value={us.story} onChange={(e) => setV({ ...v, userStories: v.userStories.map((x, j) => (j === i ? { ...x, story: e.target.value } : x)) })} />
            </Field>
            <LineArea label={undefined} value={us.ac} onChange={(x) => setV({ ...v, userStories: v.userStories.map((xx, j) => (j === i ? { ...xx, ac: x } : xx)) })} rows={3} hint="Acceptance criteria — one per row (Given/When/Then)." />
          </div>
        ))}
      </div>
      <LineArea label={undefined} value={v.uat.map((r) => r.join(" | "))}
        onChange={(x) => setV({ ...v, uat: x.filter(Boolean).map((l) => l.split("|").map((c) => c.trim())) })}
        rows={5} hint="UAT checklist — one per row: REF | scenario | expected result | (leave pass/fail empty)" />
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      <SaveBar onSave={() => save(v)} saving={saving} onReset={reset} />
    </div>
  );
}

// ---------- Images ----------

function CropModal({ file, onDone, onCancel }) {
  const [img, setImg] = useState(null);
  const [aspect, setAspect] = useState("original");
  const [zoom, setZoom] = useState(1);
  const [ox, setOx] = useState(0);
  const [oy, setOy] = useState(0);
  const canvasRef = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const renderCanvas = () => {
    if (!img || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const iw = img.width, ih = img.height;
    let cw, ch;
    if (aspect === "original") { cw = iw; ch = ih; }
    else if (aspect === "1:1") { cw = ch = Math.min(iw, ih); }
    else if (aspect === "4:5") { cw = Math.min(iw, ih * 0.8); ch = cw * 1.25; }
    else { cw = Math.min(iw, ih * 1.7778); ch = cw / 1.7778; }
    const scale = (Math.max(cw / iw, ch / ih)) * zoom;
    const sw = iw * scale, sh = ih * scale;
    const sx = (iw * scale - cw) / 2 + ox, sy = (ih * scale - ch) / 2 + oy;
    canvas.width = Math.min(cw, 1600);
    canvas.height = Math.min(ch, 1600);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, (iw - sw / scale) * 0, 0, iw, ih, -sx, -sy, sw, sh);
  };
  renderCanvas();

  const exportUrl = () => canvasRef.current.toDataURL("image/jpeg", 0.86);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4" data-testid="crop-modal">
      <div className="max-h-[92vh] w-full max-w-lg overflow-auto rounded-2xl bg-cream p-6">
        <h3 className="font-serif text-xl font-medium text-ink">Crop &amp; place image</h3>
        <div className="mt-4 space-y-4">
          <Field label="Shape">
            <select className={inputCls} value={aspect} onChange={(e) => setAspect(e.target.value)}>
              <option value="original">Original</option>
              <option value="1:1">Square (1:1)</option>
              <option value="4:5">Portrait (4:5)</option>
              <option value="16:9">Wide (16:9)</option>
            </select>
          </Field>
          <Field label={`Zoom (${zoom.toFixed(2)}×)`}>
            <input type="range" min="1" max="2.5" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Horizontal position"><input type="range" min="-300" max="300" value={ox} onChange={(e) => setOx(Number(e.target.value))} className="w-full" /></Field>
            <Field label="Vertical position"><input type="range" min="-300" max="300" value={oy} onChange={(e) => setOy(Number(e.target.value))} className="w-full" /></Field>
          </div>
          <canvas ref={canvasRef} className="w-full rounded-xl border border-line bg-white" data-testid="crop-canvas" />
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => onDone(exportUrl())} data-testid="crop-confirm" className="rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-white hover:bg-ink">
            Use this image
          </button>
          <button onClick={onCancel} className="rounded-full border border-line bg-white px-6 py-2.5 text-sm font-semibold text-ink hover:border-plum hover:text-plum">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ImagesEditor({ site }) {
  const { refresh } = useContent();
  const [images, setImages] = useState([]);
  const [pending, setPending] = useState(null);
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const { data } = await API.get("/owner/images");
      setImages(data);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the image library."));
    }
  };
  useEffect(() => { load(); }, []);
  const upload = async (dataUrl) => {
    try {
      await API.post("/owner/images", { name: pending.file.name, dataUrl, caption, alt });
      setPending(null);
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Upload failed."));
    }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-line bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Upload a new image</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Caption"><input className={inputCls} value={caption} onChange={(e) => setCaption(e.target.value)} /></Field>
          <Field label="Alternative text"><input className={inputCls} value={alt} onChange={(e) => setAlt(e.target.value)} /></Field>
        </div>
        <label className="mt-4 inline-flex cursor-pointer rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-white hover:bg-ink" data-testid="image-upload-button">
          Choose image (cropped on the next step)
          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
            const file = e.target.files[0];
            if (file) setPending({ file });
          }} />
        </label>
        <p className="mt-2 text-xs text-muted">Max 2 MB after cropping. Uploads are stored in the website database.</p>
      </div>

      <div className="rounded-2xl border border-line bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">Current About portrait</p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <img src={site.aboutPortrait.src} alt={site.aboutPortrait.alt} className="h-24 w-20 rounded-xl border border-line object-cover object-top" />
          <p className="text-xs text-muted">
            To replace it: upload an image above, then copy its URL into “About portrait → Image URL”
            on the Site text tab — or press “Set as About portrait” below any uploaded image.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-testid="image-library">
        {images.map((im) => (
          <div key={im.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-white p-4" data-testid="image-library-item">
            <img src={`/api/images/${im.id}`} alt={im.alt} className="h-16 w-16 rounded-xl border border-line object-cover" />
            <div className="min-w-[200px] flex-1">
              <p className="text-sm font-medium text-ink">{im.name}</p>
              <p className="text-xs text-muted">{im.caption || "No caption"} · /api/images/{im.id}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid={`image-set-portrait-${im.id}`}
                onClick={async () => {
                  const siteSection = { ...site, aboutPortrait: { ...site.aboutPortrait, src: `/api/images/${im.id}`, alt: im.alt || site.aboutPortrait.alt } };
                  await API.put("/owner/content/site", { value: siteSection });
                  await refresh();
                }}
                className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum"
              >
                Set as About portrait
              </button>
              <button
                data-testid={`image-set-share-${im.id}`}
                onClick={async () => {
                  const siteSection = { ...site, share: { ...site.share, image: `/api/images/${im.id}` } };
                  await API.put("/owner/content/site", { value: siteSection });
                  await refresh();
                }}
                className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum"
              >
                Set as share image
              </button>
              <button
                data-testid={`image-delete-${im.id}`}
                onClick={async () => { if (window.confirm("Delete this image?")) { await API.delete(`/owner/images/${im.id}`); load(); } }}
                className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-muted hover:border-plum hover:text-plum"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {images.length === 0 && <p className="text-sm text-muted">No uploaded images yet.</p>}
      </div>
      {error && <p role="alert" className="text-sm text-plum">{error}</p>}
      {pending && <CropModal file={pending.file} onDone={upload} onCancel={() => setPending(null)} />}
    </div>
  );
}

export default function OwnerEditor({ site, services, packages, caseStudies, dashboards, process }) {
  const tabs = [
    ["site", "Site text & headings"],
    ["services", "Services"],
    ["packages", "Packages"],
    ["cases", "Case studies"],
    ["data", "Dashboard data"],
    ["process", "Process map"],
    ["images", "Images"],
  ];
  const [tab, setTab] = useState("site");
  return (
    <div data-testid="owner-editor">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Editor sections">
        {tabs.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} data-testid={`editor-tab-${id}`}
            onClick={() => setTab(id)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              tab === id ? "bg-plum text-white" : "border border-line bg-white text-ink hover:border-plum hover:text-plum"
            }`}>
            {label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "site" && <SiteEditor key="site" site={site} />}
        {tab === "services" && <ServicesEditor key="services" services={services} groups={site.serviceGroups} />}
        {tab === "packages" && <PackagesEditor key="packages" packages={packages} />}
        {tab === "cases" && <CaseStudiesEditor key="cases" caseStudies={caseStudies} services={services} packages={packages} />}
        {tab === "data" && <DataEditor key="data" dashboards={dashboards} />}
        {tab === "process" && <ProcessEditor key="process" process={process} />}
        {tab === "images" && <ImagesEditor key="images" site={site} />}
      </div>
    </div>
  );
}
